import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { useAuthStatus, useAuthToken } from "@/contexts/OptimizedAuthContext";
import {
  ensureAuthenticated,
  fetchEntitlement,
  type Entitlement,
} from "@/services/api";
import type { Feature, Plan } from "../../../shared/entitlements";
import { UpgradeDialog, UpgradeCard } from "@/components/billing/Upgrade";

interface EntitlementContextValue {
  plan: Plan;
  loading: boolean;
  can: (feature: Feature) => boolean;
  // Open the upgrade modal for a gated feature (use at click sites that stay
  // visible to everyone).
  promptUpgrade: (feature: Feature) => void;
}

const EntitlementContext = createContext<EntitlementContextValue | null>(null);

const FREE: Entitlement = { plan: "free", features: {} };

export function EntitlementProvider({ children }: { children: ReactNode }) {
  const { isSignedIn, isLoaded } = useAuthStatus();
  const getToken = useAuthToken();
  const [upgradeFeature, setUpgradeFeature] = useState<Feature | null>(null);

  // Fetch once into cache; revalidate on window focus (cheap, catches changes
  // made elsewhere). The Worker is the real enforcement, so a stale cache is
  // only ever a cosmetic glitch.
  const { data, isLoading } = useQuery({
    queryKey: ["entitlement"],
    enabled: isLoaded && isSignedIn,
    refetchOnWindowFocus: true,
    staleTime: 60_000,
    queryFn: async () => {
      await ensureAuthenticated(getToken);
      return fetchEntitlement();
    },
  });

  const ent = data ?? FREE;

  // Stable value identity (keyed on the entitlement + loading) so toggling the
  // local upgrade dialog doesn't cascade re-renders across every consumer.
  // setUpgradeFeature from useState is already stable.
  const value = useMemo(
    () => ({
      plan: ent.plan,
      loading: isLoading,
      can: (feature: Feature) => ent.features?.[feature] === true,
      promptUpgrade: setUpgradeFeature,
    }),
    [ent, isLoading],
  );

  return (
    <EntitlementContext.Provider value={value}>
      {children}
      <UpgradeDialog
        feature={upgradeFeature}
        open={upgradeFeature !== null}
        onClose={() => setUpgradeFeature(null)}
      />
    </EntitlementContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useEntitlement(): EntitlementContextValue {
  const ctx = useContext(EntitlementContext);
  if (!ctx)
    throw new Error("useEntitlement must be used within an EntitlementProvider");
  return ctx;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useCan(feature: Feature): boolean {
  return useEntitlement().can(feature);
}

// Route guard: render children only if entitled, else an inline upgrade panel.
// Prevents direct navigation (e.g. typing /skills) from exposing the feature.
export function RequireFeature({
  feature,
  children,
}: {
  feature: Feature;
  children: ReactNode;
}) {
  const { can, loading } = useEntitlement();
  if (loading) return null; // avoid flashing the upgrade panel before load
  if (can(feature)) return <>{children}</>;
  return <UpgradeCard feature={feature} />;
}
